import React, { useState } from 'react';
import EventIcon, { iconOf, stripIcon } from './EventIcon';
import { CheckSquare } from './RoundBtn';
import { uses12h } from '../utils/timeFormat';
import DialogCard from './DialogCard';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useT, DATE_LOCALES } from '../i18n';
import apiClient from '../api/client';
import { invalidateEventCache } from '../utils/viewCache';
import { isBirthday } from '../utils/birthday';
import { TrashIcon, ClipIcon } from './icons';

// NOTE: the "All day" wording lives in i18n.js as T.form.allDay. It used to be
// copied into a private table here, into another one in EventForm and read from
// i18n in MultiEventReview — three spellings of the same two words, free to
// drift apart at the next translation pass.

// Inline labels for the share sheet (IT/EN/ES; others fall back to EN)
const SHARE_LABELS = {
  it: { title: 'Condividi evento', shareBtn: 'Condividi (SMS, WhatsApp…)', orEmail: 'oppure invia per email', placeholder: 'email del destinatario', send: 'Invia', sending: 'Invio…', sent: 'Email inviata ✓', cancel: 'Chiudi', invalid: 'Email non valida', notConfigured: 'Invio email non disponibile su questo server', error: 'Qualcosa è andato storto, riprova', copied: 'Copiato negli appunti', action: 'Condividi' },
  en: { title: 'Share event', shareBtn: 'Share (SMS, WhatsApp…)', orEmail: 'or send by email', placeholder: 'recipient email', send: 'Send', sending: 'Sending…', sent: 'Email sent ✓', cancel: 'Close', invalid: 'Invalid email', notConfigured: 'Email sending not available on this server', error: 'Something went wrong, try again', copied: 'Copied to clipboard', action: 'Share' },
  es: { title: 'Compartir evento', shareBtn: 'Compartir (SMS, WhatsApp…)', orEmail: 'o enviar por email', placeholder: 'email del destinatario', send: 'Enviar', sending: 'Enviando…', sent: 'Email enviado ✓', cancel: 'Cerrar', invalid: 'Email no válido', notConfigured: 'Envío de email no disponible en este servidor', error: 'Algo salió mal, inténtalo de nuevo', copied: 'Copiado al portapapeles', action: 'Compartir' },
  de: { title: 'Termin teilen', shareBtn: 'Teilen (SMS, WhatsApp…)', orEmail: 'oder per E-Mail senden', placeholder: 'E-Mail des Empfängers', send: 'Senden', sending: 'Senden…', sent: 'E-Mail gesendet ✓', cancel: 'Schließen', invalid: 'Ungültige E-Mail', notConfigured: 'E-Mail-Versand auf diesem Server nicht verfügbar', error: 'Etwas ist schiefgelaufen, versuche es erneut', copied: 'In die Zwischenablage kopiert', action: 'Teilen' },
  fr: { title: 'Partager l’événement', shareBtn: 'Partager (SMS, WhatsApp…)', orEmail: 'ou envoyer par e-mail', placeholder: 'e-mail du destinataire', send: 'Envoyer', sending: 'Envoi…', sent: 'E-mail envoyé ✓', cancel: 'Fermer', invalid: 'E-mail invalide', notConfigured: 'Envoi d’e-mail non disponible sur ce serveur', error: 'Une erreur est survenue, réessayez', copied: 'Copié dans le presse-papiers', action: 'Partager' },
  nl: { title: 'Afspraak delen', shareBtn: 'Delen (sms, WhatsApp…)', orEmail: 'of per e-mail versturen', placeholder: 'e-mail van ontvanger', send: 'Versturen', sending: 'Versturen…', sent: 'E-mail verzonden ✓', cancel: 'Sluiten', invalid: 'Ongeldig e-mailadres', notConfigured: 'E-mail versturen niet beschikbaar op deze server', error: 'Er ging iets mis, probeer opnieuw', copied: 'Gekopieerd naar klembord', action: 'Delen' },
  pt: { title: 'Partilhar evento', shareBtn: 'Partilhar (SMS, WhatsApp…)', orEmail: 'ou enviar por email', placeholder: 'email do destinatário', send: 'Enviar', sending: 'A enviar…', sent: 'Email enviado ✓', cancel: 'Fechar', invalid: 'Email inválido', notConfigured: 'Envio de email não disponível neste servidor', error: 'Algo correu mal, tente novamente', copied: 'Copiado para a área de transferência', action: 'Partilhar' },
  ru: { title: 'Поделиться событием', shareBtn: 'Поделиться (SMS, WhatsApp…)', orEmail: 'или отправить по эл. почте', placeholder: 'эл. почта получателя', send: 'Отправить', sending: 'Отправка…', sent: 'Письмо отправлено ✓', cancel: 'Закрыть', invalid: 'Неверный адрес', notConfigured: 'Отправка почты недоступна на этом сервере', error: 'Что-то пошло не так, попробуйте ещё раз', copied: 'Скопировано в буфер обмена', action: 'Поделиться' },
  pl: { title: 'Udostępnij wydarzenie', shareBtn: 'Udostępnij (SMS, WhatsApp…)', orEmail: 'lub wyślij e-mailem', placeholder: 'e-mail odbiorcy', send: 'Wyślij', sending: 'Wysyłanie…', sent: 'E-mail wysłany ✓', cancel: 'Zamknij', invalid: 'Nieprawidłowy e-mail', notConfigured: 'Wysyłanie e-maili niedostępne na tym serwerze', error: 'Coś poszło nie tak, spróbuj ponownie', copied: 'Skopiowano do schowka', action: 'Udostępnij' },
  ro: { title: 'Distribuie evenimentul', shareBtn: 'Distribuie (SMS, WhatsApp…)', orEmail: 'sau trimite pe email', placeholder: 'email-ul destinatarului', send: 'Trimite', sending: 'Se trimite…', sent: 'Email trimis ✓', cancel: 'Închide', invalid: 'Email invalid', notConfigured: 'Trimiterea de email nu este disponibilă pe acest server', error: 'Ceva n-a mers, încearcă din nou', copied: 'Copiat în clipboard', action: 'Distribuie' },
  zh: { title: '分享日程', shareBtn: '分享（短信、WhatsApp…）', orEmail: '或通过邮件发送', placeholder: '收件人邮箱', send: '发送', sending: '发送中…', sent: '邮件已发送 ✓', cancel: '关闭', invalid: '邮箱无效', notConfigured: '此服务器不支持发送邮件', error: '出错了，请重试', copied: '已复制到剪贴板', action: '分享' },
  ja: { title: '予定を共有', shareBtn: '共有（SMS・WhatsApp…）', orEmail: 'またはメールで送信', placeholder: '宛先のメールアドレス', send: '送信', sending: '送信中…', sent: 'メールを送信しました ✓', cancel: '閉じる', invalid: 'メールアドレスが無効です', notConfigured: 'このサーバーではメール送信を利用できません', error: 'エラーが発生しました。もう一度お試しください', copied: 'クリップボードにコピーしました', action: '共有' },
  hi: { title: 'इवेंट साझा करें', shareBtn: 'साझा करें (SMS, WhatsApp…)', orEmail: 'या ईमेल से भेजें', placeholder: 'प्राप्तकर्ता का ईमेल', send: 'भेजें', sending: 'भेजा जा रहा है…', sent: 'ईमेल भेजा गया ✓', cancel: 'बंद करें', invalid: 'अमान्य ईमेल', notConfigured: 'इस सर्वर पर ईमेल भेजना उपलब्ध नहीं है', error: 'कुछ गलत हुआ, फिर से कोशिश करें', copied: 'क्लिपबोर्ड पर कॉपी किया गया', action: 'साझा करें' },
  ko: { title: '일정 공유', shareBtn: '공유 (SMS, WhatsApp…)', orEmail: '또는 이메일로 보내기', placeholder: '받는 사람 이메일', send: '보내기', sending: '보내는 중…', sent: '이메일 전송 완료 ✓', cancel: '닫기', invalid: '잘못된 이메일', notConfigured: '이 서버에서는 이메일 전송을 사용할 수 없습니다', error: '문제가 발생했습니다. 다시 시도해 주세요', copied: '클립보드에 복사됨', action: '공유' },
  ar: { title: 'مشاركة الحدث', shareBtn: 'مشاركة (SMS، WhatsApp…)', orEmail: 'أو أرسل بالبريد الإلكتروني', placeholder: 'بريد المستلم', send: 'إرسال', sending: 'جارٍ الإرسال…', sent: 'تم إرسال البريد ✓', cancel: 'إغلاق', invalid: 'بريد غير صالح', notConfigured: 'إرسال البريد غير متاح على هذا الخادم', error: 'حدث خطأ ما، حاول مجددًا', copied: 'تم النسخ إلى الحافظة', action: 'مشاركة' },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// hideSubCalendar: drop the label chip from the card. Used by the label page,
// where every single card carries the same label as the page title — repeating
// it on each row is noise, not information.
export default function EventCard({ event, onDelete, hideSubCalendar = false }) {
  const { user } = useAuth();
  const T = useT(user?.language);
  const { id, title, priority, start, location, notes, subCalendar, recurringEventId, sharedGroupName, sharedGroupId, allDay, hasAttachments } = event;
  const [deleting, setDeleting] = useState(false);
  // null | 'single' | 'recurring' — which delete confirmation is on screen.
  // 'single' used to be a window.confirm: a grey system box, in the system
  // language, in the middle of an app that has its own look everywhere else.
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  // ── Share state ──
  const baseLang = (user?.language || 'it').split('-')[0];
  const L = SHARE_LABELS[baseLang] || SHARE_LABELS.en;
  const locale = DATE_LOCALES[user?.language] || DATE_LOCALES['it'];
  const [showShare, setShowShare] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareStatus, setShareStatus] = useState('idle'); // idle | sending | sent | error
  const [shareErr, setShareErr] = useState('');
  const [shareOk,  setShareOk]  = useState(''); // good news ("copied") — NOT an error

  function buildShareText() {
    let whenStr = '';
    try {
      if (allDay) {
        whenStr = `${format(new Date(`${String(start).slice(0, 10)}T12:00:00`), 'EEEE d MMMM', { locale })} · ${T.form.allDay}`;
      } else if (start) {
        whenStr = format(new Date(start), 'EEEE d MMMM, HH:mm', { locale });
      }
    } catch { whenStr = ''; }
    return [
      `📅 ${title}`,
      whenStr,
      location ? `📍 ${location}` : '',
      notes || '',
    ].filter(Boolean).join('\n');
  }

  // Copy to the clipboard as the fallback path (and as the rescue path when the
  // share sheet itself refuses to open).
  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      setShareOk(L.copied); setShareErr('');
    } catch {
      setShareErr(L.error); setShareOk('');
    }
  }

  async function handleNativeShare() {
    const text = buildShareText();
    if (!navigator.share) { await copyToClipboard(text); return; }
    try {
      await navigator.share({ title, text });
      setShowShare(false);
    } catch (err) {
      // Only AbortError means "the user changed their mind". Everything else is
      // a real failure, and treating it as a cancellation made the sheet close
      // as if the event had been shared when nothing had left the phone.
      if (err?.name === 'AbortError') { setShowShare(false); return; }
      await copyToClipboard(text);   // fall back instead of failing silently
    }
  }

  async function handleSendEmail() {
    const to = shareEmail.trim();
    if (!EMAIL_RE.test(to)) { setShareStatus('error'); setShareErr(L.invalid); return; }
    setShareStatus('sending'); setShareErr('');
    try {
      await apiClient.post('/events/share/email', { to, event, lang: user?.language });
      setShareStatus('sent');
    } catch (e) {
      setShareStatus('error');
      setShareErr(e.response?.data?.error === 'email_not_configured' ? L.notConfigured : L.error);
    }
  }

  function closeShare() {
    setShowShare(false);
    setShareEmail(''); setShareStatus('idle'); setShareErr(''); setShareOk('');
  }

  // How long a multi-day event lasts. Without this, a holiday running 1–16
  // August showed as an ordinary "all day" card on the 14th with no hint that
  // it goes on: the only way to learn it ran until the 16th was to open it.
  // The end of an all-day event is exclusive (1 → 17 means "up to the 16th").
  let untilStr = '';
  try {
    if (event.end) {
      const startDay = allDay ? String(start).slice(0, 10)
        : format(new Date(start), 'yyyy-MM-dd');
      let lastDay;
      if (allDay) {
        const [y, m, d] = String(event.end).slice(0, 10).split('-').map(Number);
        const prev = new Date(Date.UTC(y, m - 1, d - 1));
        lastDay = prev.toISOString().slice(0, 10);
      } else {
        lastDay = format(new Date(event.end), 'yyyy-MM-dd');
      }
      if (lastDay > startDay) {
        untilStr = format(new Date(`${lastDay}T12:00:00`), 'd MMM', { locale });
      }
    }
  } catch { untilStr = ''; }

  let timeStr = '';
  if (allDay) {
    timeStr = T.form.allDay;
  } else if (start) {
    try {
      const lang = user?.language || 'it';
      const use12h = uses12h(lang);
      timeStr = new Date(start).toLocaleTimeString(lang, {
        hour: '2-digit', minute: '2-digit', hour12: use12h,
      });
    } catch {
      timeStr = '';
    }
  }

  function handleDelete() {
    setDeleteError('');
    // Same inline modal in both cases — a recurring event just gets two buttons
    // instead of one.
    setConfirmDelete(recurringEventId ? 'recurring' : 'single');
  }

  async function doDelete(eventId) {
    setDeleting(true);
    setConfirmDelete(null);
    try {
      await apiClient.delete(`/events/${eventId}`);
      invalidateEventCache(); // so Today refetches instead of showing the deleted event from cache
      if (onDelete) onDelete(id);
    } catch (err) {
      setDeleting(false);
      // Inline, in the card, like everywhere else in the app — an alert() box
      // was the only place a failure was announced by the operating system.
      setDeleteError(T.form.deleteError);
    }
  }

  return (
    <div
      className={`event-card priority-${priority || 'green'}`}
      /**
       * A wash of the label's colour behind the whole card.
       *
       * Jodi, comparing MeMo with Google: "the problem with everything being
       * one colour is that everything blends together. I can't see when one
       * event starts and the next ends." She is right, and the answer is not
       * to copy Google's coloured blocks — it is to let MeMo's own labels do
       * the work they were already doing on the chip.
       *
       * A wash and not coloured TEXT, which was the first idea: these colours
       * are measured, and as ink on a light background the yellows sit at 1.5
       * against the page. A title in one of them would be a title nobody can
       * read. Behind the words at 25% they are unmistakable and cost the text
       * nothing.
       *
       * color-mix does the arithmetic against whatever --color-card currently
       * is, so the same line works in the light theme and the dark one and
       * follows the accent when it changes. A browser too old for it ignores
       * the declaration and gets the plain card, which is exactly today.
       */
      style={subCalendar?.color
        ? { background: `color-mix(in srgb, ${subCalendar.color} 25%, var(--color-card))` }
        : undefined}
    >
      {/* Delete confirmation — one modal, both cases */}
      {confirmDelete && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}
          onClick={() => setConfirmDelete(null)}
        >
          <DialogCard
            style={{
              background: 'var(--color-card)', borderRadius: 'var(--radius)',
              padding: '24px 20px', width: '100%', maxWidth: 340,
              boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <p style={{ margin: '0 0 18px', fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-text)' }}>
              {confirmDelete === 'recurring' ? `🔁 ${T.form.deleteRecurring}` : T.form.deleteConfirm}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {confirmDelete === 'recurring' ? (
                <>
                  <button className="btn btn-outline" onClick={() => doDelete(id)}>
                    {T.form.deleteOnlyThis}
                  </button>
                  <button
                    className="btn btn-outline"
                    style={{ borderColor: 'var(--color-stripe-red)', color: 'var(--color-stripe-red)' }}
                    onClick={() => doDelete(recurringEventId)}
                  >
                    {T.form.deleteAllSeries}
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-outline"
                  style={{ borderColor: 'var(--color-stripe-red)', color: 'var(--color-stripe-red)' }}
                  onClick={() => doDelete(id)}
                >
                  {T.form.deleteButton}
                </button>
              )}
              <button className="btn btn-outline btn-sm" onClick={() => setConfirmDelete(null)}>
                {T.form.cancelButton || 'Cancel'}
              </button>
            </div>
          </DialogCard>
        </div>
      )}

      {/* Share sheet — native share (SMS/WhatsApp/…) + send by email */}
      {showShare && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}
          onClick={closeShare}
        >
          <DialogCard
            style={{
              background: 'var(--color-card)', borderRadius: 'var(--radius)',
              padding: '22px 20px', width: '100%', maxWidth: 360,
              boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <p style={{ margin: '0 0 16px', fontWeight: 800, fontSize: '1rem', color: 'var(--color-text)' }}>
              📨 {L.title}
            </p>

            {shareStatus === 'sent' ? (
              <>
                <div style={{ textAlign: 'center', padding: '8px 0 16px', fontSize: '1rem', color: 'var(--color-primary)', fontWeight: 700 }}>
                  {L.sent}
                </div>
                <button className="btn btn-outline btn-sm" style={{ width: '100%' }} onClick={closeShare}>
                  {L.cancel}
                </button>
              </>
            ) : (
              <>
                {/* Native share — opens the phone's share sheet (free: SMS, WhatsApp, email…) */}
                <button
                  className="btn"
                  style={{ width: '100%', background: 'var(--color-primary)', color: '#fff', border: 'none', marginBottom: 16 }}
                  onClick={handleNativeShare}
                >
                  {L.shareBtn}
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 12px' }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{L.orEmail}</span>
                  <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="email"
                    value={shareEmail}
                    onChange={e => { setShareEmail(e.target.value); if (shareStatus === 'error') { setShareStatus('idle'); setShareErr(''); } }}
                    placeholder={L.placeholder}
                    autoComplete="email"
                    style={{
                      flex: 1, minWidth: 0, padding: '11px 12px', fontSize: '0.95rem',
                      border: '1.5px solid var(--color-border)', borderRadius: 10,
                      background: 'var(--color-surface)', color: 'var(--color-text)',
                      outline: 'none', fontFamily: 'inherit',
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleSendEmail}
                    disabled={shareStatus === 'sending'}
                    style={{
                      flexShrink: 0, background: 'var(--color-primary-dark)', color: '#fff',
                      border: 'none', borderRadius: 10, padding: '0 18px', fontWeight: 700,
                      fontSize: '0.9rem', cursor: shareStatus === 'sending' ? 'wait' : 'pointer',
                    }}
                  >
                    {shareStatus === 'sending' ? L.sending : L.send}
                  </button>
                </div>

                {shareOk && (
                  <div style={{ color: 'var(--color-primary)', fontWeight: 700, fontSize: '0.8rem', marginTop: 8 }}>{shareOk}</div>
                )}
                {shareErr && (
                  <div style={{ color: '#c0392b', fontSize: '0.8rem', marginTop: 8 }}>{shareErr}</div>
                )}

                <button className="btn btn-outline btn-sm" style={{ width: '100%', marginTop: 16 }} onClick={closeShare}>
                  {L.cancel}
                </button>
              </>
            )}
          </DialogCard>
        </div>
      )}

      {/* Top row: time + title (left, fully tappable) | actions + subcal badge (right) */}
      <div className="event-card-top">
        {/* The when: the hour, in a column of fixed width so that every title
            down the day begins at the same place.
            Silvia's design and it does two things at once. It is a line
            shorter than the label sitting under the whole card, which is where
            it was an hour ago; and it means somebody who never uses colours
            can still tell one event from another and still reach the label's
            agenda — "l'etichetta la scriverei allineata all'orario così se un
            utente decide di non usare i colori può comunque distinguere
            l'evento ed aprire il sottocalendario".

            It sits OUTSIDE the card's main link, and it has to: a link inside
            a link is not something a browser can make sense of, and the label
            has somewhere else to go. */}
        <div className="event-when">
          {/* An all-day event used to spend the whole time slot saying "Tutto
              il giorno" — three words where the eye was looking for a number.
              Now it is the same box you ticked in the +. The words are still
              there for a screen reader, which cannot see a box. */}
          {allDay
            ? <><CheckSquare on size={17} />
                <span className="visually-hidden">{T.form.allDay}</span></>
            : <span className="event-time">{timeStr}</span>}
        </div>
        {/* The icon chosen in the +, drawn by us so it is the same picture on
            every phone — and now a way in: it opens everything still to come
            carrying that same drawing. "Quando sono i compleanni?" is a
            question the picture can answer, and it is the third filter built
            out of one screen, after labels and shared calendars.

            It sits outside the card's main link because a link inside a link
            is not something a browser can make sense of. A birthday recognised
            from the word alone still gets its cake, and that one is NOT a link:
            nobody chose it, so there is no icon to ask about. */}
        {iconOf(title) ? (
          <Link
            to={`/icon/${encodeURIComponent(iconOf(title))}`}
            aria-label={T.form?.iconLabel || 'Icona'}
            style={{ display: 'flex', alignItems: 'center', flexShrink: 0, marginInlineEnd: 6, color: 'inherit' }}
          >
            <EventIcon icon={iconOf(title)} size={26} />
          </Link>
        ) : isBirthday(title) ? (
          <EventIcon icon="🎂" size={26} style={{ marginInlineEnd: 6, flexShrink: 0 }} />
        ) : null}
        <Link
          to={`/edit/${id}`}
          state={{ event }}
          className="event-card-main"
          style={{ textDecoration: 'none', color: 'inherit', flex: 1, minWidth: 0 }}
        >
          <span className="event-title" translate="no">{stripIcon(title)}</span>
          {untilStr && (
            <span className="event-time" style={{ opacity: 0.85 }}>→ {untilStr}</span>
          )}
        </Link>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0 }}>
          <div className="event-card-actions">
            {hasAttachments && (
              <Link
                to={`/edit/${id}`}
                state={{ event, unlock: true }}
                className="event-action-btn"
                title={T.form.attLabel}
                style={{ color: 'var(--color-primary-dark)' }}
              >
                <ClipIcon size={18} />
              </Link>
            )}
            <button
              type="button"
              className="event-action-btn"
              onClick={() => setShowShare(true)}
              title={L.action}
              style={{ opacity: 1, color: 'var(--color-primary-dark)' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="4.5" width="20" height="15" rx="2.5" />
                <path d="m2.5 6.5 9.5 6 9.5-6" />
              </svg>
            </button>
            <button
              type="button"
              className="event-action-btn danger"
              onClick={handleDelete}
              disabled={deleting}
              title={T.form.deleteButton}
            >
              {deleting ? '…' : <TrashIcon size={19} />}
            </button>
          </div>
        </div>
      </div>

      {/* On a line of its own, under the top row, starting at the same edge
          as the hour. It used to live inside the 64px time column, where a
          name of any length was cut — Silvia: "non si leggono per intero le
          etichette e il calendario condiviso". The column stays fixed so that
          every title begins at the same place; the label simply stopped living
          in it and now has the width of the card to be read in.

          Label and calendar stay side by side: WHICH label and WHOSE calendar,
          taken in together as the event address. */}
      {((subCalendar && !hideSubCalendar) || sharedGroupName) && (
        <span className="event-label-row">
          {subCalendar && !hideSubCalendar && (
            <Link
              to={`/label/${subCalendar.id}`}
              title={subCalendar.name}
              className="event-label"
              style={{ color: subCalendar.color }}
            >
              {subCalendar.name}
            </Link>
          )}
          {subCalendar && !hideSubCalendar && sharedGroupName && (
            <span className="event-label" style={{ color: 'var(--color-text-muted)', opacity: 0.7 }}>·</span>
          )}
          {/* Every place the calendar's name appears is a way into it, not
              only on somebody else's events. Silvia: "io voglio poter
              accedere alla schermata da ogni titolo Calendario condiviso su
              ogni evento dove vedo il titolo". */}
          {sharedGroupName && (sharedGroupId ? (
            <Link
              to={`/shared/${sharedGroupId}`}
              className="event-label"
              title={sharedGroupName}
              style={{ color: 'var(--color-text-muted)' }}
            >
              {sharedGroupName}
            </Link>
          ) : (
            <span className="event-label" style={{ color: 'var(--color-text-muted)' }}>{sharedGroupName}</span>
          ))}
        </span>
      )}


      {/* Location — always clickable: URLs open directly, text opens Google Maps */}
      {location && (
        <div className="event-location">
          📍{' '}
          <a
            href={/^https?:\/\//i.test(location) ? location : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--color-primary)', wordBreak: 'break-all' }}
          >
            {location}
          </a>
        </div>
      )}

      {/* Notes — max 3 lines */}
      {notes && (
        <div className="event-notes">{notes}</div>
      )}

      {/* The delete did not go through — said here, in the card, and not by a
          system alert box that vanishes with one tap */}
      {deleteError && (
        <div style={{ marginTop: 6, fontSize: '0.78rem', fontWeight: 700, color: '#c0392b' }}>
          {deleteError}
        </div>
      )}
    </div>
  );
}
